/**
 * Admin Layout Component
 *
 * Layout wrapper for admin pages with admin-specific navigation
 */

import { type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/utils';
import { useAuthStore, useIsSuperAdmin } from '@/stores/authStore';
import { APP_NAME, ROUTES } from '@/constants';
import {
  Users,
  DollarSign,
  Tag,
  BarChart3,
  FileText,
  Home,
  LogOut,
  Shield,
} from 'lucide-react';

interface NavItem {
  label: string;
  icon: ReactNode;
  path: string;
}

const adminNavItems: NavItem[] = [
  {
    label: 'Overview',
    icon: <Home className="h-5 w-5" />,
    path: ROUTES.ADMIN,
  },
  {
    label: 'Customers',
    icon: <Users className="h-5 w-5" />,
    path: ROUTES.ADMIN_CUSTOMERS,
  },
  {
    label: 'Analytics',
    icon: <BarChart3 className="h-5 w-5" />,
    path: ROUTES.ADMIN_ANALYTICS,
  },
  {
    label: 'Promotions',
    icon: <Tag className="h-5 w-5" />,
    path: ROUTES.ADMIN_PROMOTIONS,
  },
  {
    label: 'Audit Log',
    icon: <FileText className="h-5 w-5" />,
    path: ROUTES.ADMIN_AUDIT_LOG,
  },
];

interface AdminLayoutProps {
  children: ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const logout = useAuthStore((state) => state.logout);
  const user = useAuthStore((state) => state.user);
  const isSuperAdmin = useIsSuperAdmin();

  // Wait for auth to load before checking admin status
  const isLoading = useAuthStore((state) => state.isLoading);
  if (isLoading || !user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  // Redirect if not super admin
  if (!isSuperAdmin) {
    window.location.href = ROUTES.DASHBOARD;
    return null;
  }

  const isActivePath = (path: string): boolean => {
    if (path === ROUTES.ADMIN) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Admin Header */}
      <header className="bg-gradient-to-r from-purple-600 to-indigo-600 border-b border-purple-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Title */}
            <div className="flex items-center gap-4">
              <Link to={ROUTES.ADMIN} className="flex items-center gap-3">
                <Shield className="h-8 w-8 text-white" />
                <div>
                  <h1 className="text-xl font-bold text-white">{APP_NAME} Admin</h1>
                  <p className="text-xs text-purple-200">System Administration</p>
                </div>
              </Link>
            </div>

            {/* User Info */}
            <div className="flex items-center gap-4">
              <Link
                to={ROUTES.DASHBOARD}
                className="text-sm text-purple-100 hover:text-white transition-colors"
              >
                ← Back to App
              </Link>
              {user && (
                <div className="flex items-center gap-3 pl-4 border-l border-purple-400">
                  <div className="h-8 w-8 bg-purple-300 rounded-full flex items-center justify-center text-purple-900 font-medium">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="hidden sm:block">
                    <p className="text-sm font-medium text-white">{user.name}</p>
                    <p className="text-xs text-purple-200">{user.email}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-6">
          {/* Admin Sidebar */}
          <aside className="w-64 shrink-0">
            <nav className="bg-white rounded-lg border border-slate-200 p-3">
              <div className="space-y-1">
                {adminNavItems.map((item) => {
                  const isActive = isActivePath(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                        isActive
                          ? 'bg-purple-50 text-purple-700 font-medium'
                          : 'text-slate-600 hover:bg-slate-50'
                      )}
                    >
                      <span className={cn(isActive && 'text-purple-600')}>
                        {item.icon}
                      </span>
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">{children}</main>
        </div>
      </div>
    </div>
  );
}
